import { AdSize, SizeMappingEntry } from './gpt';

/**
 * Central configuration for web display ads (Google Ad Manager / GPT).
 *
 * Runtime on/off gating lives in feature flags (see
 * Webapp/shared/concepts/feature-flags.ts, ADS group) which are controlled
 * without a deploy via the dynconfig-published featureFlags.json fetched from
 * FLAP_STATIC_URL on every request. The values below (network code, ad unit
 * paths, sizes) are build-time constants with the agreed GAM defaults;
 * changing a path requires a deploy.
 */

export const GAM_NETWORK_CODE = '21709104563';

export const AD_UNIT_PATHS = {
  leaderboard: `/${GAM_NETWORK_CODE}/web/leaderboard`,
  infeed: `/${GAM_NETWORK_CODE}/web/infeed`,
  articleRail: `/${GAM_NETWORK_CODE}/web/article_rail`,
};

// Leaderboard: 970x90 / 728x90 on desktop, 728x90 on tablet, 320x50 on
// narrow viewports. The widest set is also passed to defineSlot as the base
// size list; the mapping is what GPT actually uses per viewport.
export const LEADERBOARD_SIZES: AdSize[] = [
  [970, 90],
  [728, 90],
  [320, 50],
];
export const LEADERBOARD_SIZE_MAPPING: SizeMappingEntry[] = [
  {
    viewport: [1024, 0],
    sizes: [
      [970, 90],
      [728, 90],
    ],
  },
  { viewport: [768, 0], sizes: [[728, 90]] },
  { viewport: [0, 0], sizes: [[320, 50]] },
];

export const MEDIUM_RECTANGLE_SIZES: AdSize[] = [[300, 250]];

export const DEFAULT_INFEED_AD_INTERVAL = 9;

type FeatureFlags = Partial<Record<string, boolean>>;

export const isWebAdsEnabled = (featureFlags?: FeatureFlags): boolean =>
  !!featureFlags?.WEB_ADS;

export const isLeaderboardAdEnabled = (featureFlags?: FeatureFlags): boolean =>
  isWebAdsEnabled(featureFlags) && !!featureFlags?.WEB_ADS_LEADERBOARD;

export const isInfeedAdEnabled = (featureFlags?: FeatureFlags): boolean =>
  isWebAdsEnabled(featureFlags) && !!featureFlags?.WEB_ADS_INFEED;

export const isArticleRailAdEnabled = (featureFlags?: FeatureFlags): boolean =>
  isWebAdsEnabled(featureFlags) && !!featureFlags?.WEB_ADS_ARTICLE_RAIL;

/**
 * flint (in-house server-side header bidding) pre-bid on display slots:
 * when active, slots query flint before GPT and forward the winning bid as
 * GAM key-values (see ./flint.ts).
 */
export const isFlintHeaderBiddingEnabled = (
  featureFlags?: FeatureFlags,
): boolean => isWebAdsEnabled(featureFlags) && !!featureFlags?.WEB_ADS_FLINT;

/**
 * Ads identity, phase 1 (see ./identity.ts): GPT PPID + LiveRamp RampID
 * envelope for logged-in users, consent permitting.
 */
export const isAdsIdentityEnabled = (featureFlags?: FeatureFlags): boolean =>
  isWebAdsEnabled(featureFlags) && !!featureFlags?.WEB_ADS_IDENTITY;

/**
 * In-feed insertion interval ("an ad card every N story tiles"). The feature
 * flag mechanism is boolean-only, so the configurable interval is expressed
 * as discrete override flags; with none active the default (9) applies.
 */
export const getInfeedAdInterval = (featureFlags?: FeatureFlags): number => {
  if (featureFlags?.WEB_ADS_INFEED_INTERVAL_3) {
    return 3;
  }
  if (featureFlags?.WEB_ADS_INFEED_INTERVAL_6) {
    return 6;
  }
  if (featureFlags?.WEB_ADS_INFEED_INTERVAL_12) {
    return 12;
  }
  return DEFAULT_INFEED_AD_INTERVAL;
};
